import api from './axios';

// Get all radar entries
export const getAccounts = async (params = {}) => {
  try {
    const response = await api.get('/accounts', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching radar entries:', error);
    throw error.response?.data?.message || 'Failed to fetch radar entries';
  }
};

// Preview CSV file to get headers and sample data
export const previewCSV = async (file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/accounts/preview-csv', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error previewing CSV:', error);
    throw error.response?.data?.message || 'Failed to preview CSV file';
  }
};

// Upload CSV file with field mappings
export const uploadCSV = async (file, fieldMappings = {}, customFields = {}) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('fieldMappings', JSON.stringify(fieldMappings));
    formData.append('customFields', JSON.stringify(customFields));

    const response = await api.post('/accounts/upload-csv', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading CSV:', error);
    throw error.response?.data?.message || 'Failed to upload CSV file';
  }
};

// Get HubSpot import preview
export const getHubSpotPreview = async (params = {}) => {
  try {
    const response = await api.get('/accounts/import-hubspot-preview', { params });
    return response.data;
  } catch (error) {
    console.error('Error getting HubSpot preview:', error);
    throw error.response?.data?.message || 'Failed to get HubSpot preview';
  }
};

// Import from HubSpot with optional field mappings
export const importFromHubSpot = async (selectedCompanies = [], fieldMappings = {}, customFields = {}) => {
  try {
    const response = await api.post('/accounts/import-hubspot', {
      selectedCompanies,
      fieldMappings,
      customFields
    });
    return response.data;
  } catch (error) {
    console.error('Error importing from HubSpot:', error);
    throw error.response?.data?.message || 'Failed to import from HubSpot';
  }
};

// Import from Salesforce
export const importFromSalesforce = async () => {
  try {
    const response = await api.post('/accounts/import-salesforce');
    return response.data;
  } catch (error) {
    console.error('Error importing from Salesforce:', error);
    throw error.response?.data?.message || 'Failed to import from Salesforce';
  }
};

// Get account by ID
export const getAccount = async (id) => {
  try {
    const response = await api.get(`/accounts/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching account:', error);
    throw error.response?.data?.message || 'Failed to fetch account';
  }
};

// Update account
export const updateAccount = async (id, data) => {
  try {
    const response = await api.put(`/accounts/${id}`, data);
    return response.data;
  } catch (error) {
    console.error('Error updating account:', error);
    const errorMessage = error.response?.data?.message || 'Failed to update account';
    const errorObj = new Error(errorMessage);
    errorObj.response = error.response; // Preserve response for debugging
    throw errorObj;
  }
};

// Delete account
export const deleteAccount = async (id) => {
  try {
    const response = await api.delete(`/accounts/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting account:', error);
    throw error.response?.data?.message || 'Failed to delete account';
  }
};

// Get account statistics
export const getAccountStats = async () => {
  try {
    const response = await api.get('/accounts/statistics/overview');
    return response.data;
  } catch (error) {
    console.error('Error fetching account stats:', error);
    throw error.response?.data?.message || 'Failed to fetch account statistics';
  }
};

// Get unique industry values
export const getIndustries = async () => {
  try {
    const response = await api.get('/accounts/industries');
    return response.data;
  } catch (error) {
    console.error('Error fetching industries:', error);
    throw error.response?.data?.message || 'Failed to fetch industries';
  }
};

// Get unique deal stage values
export const getDealStages = async () => {
  try {
    const response = await api.get('/accounts/deal-stages');
    return response.data;
  } catch (error) {
    console.error('Error fetching deal stages:', error);
    throw error.response?.data?.message || 'Failed to fetch deal stages';
  }
};

// Get all contacts
export const getContacts = async (params = {}) => {
  try {
    const response = await api.get('/contacts', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching contacts:', error);
    throw error.response?.data?.message || 'Failed to fetch contacts';
  }
};

// Preview contacts CSV file to get headers and sample data
export const previewContactsCSV = async (file) => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post('/contacts/preview-csv', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error previewing contacts CSV:', error);
    throw error.response?.data?.message || 'Failed to preview contacts CSV file';
  }
};

// Upload contacts CSV file with field mappings
export const uploadContactsCSV = async (file, fieldMappings = {}, customFields = {}) => {
  try {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('fieldMappings', JSON.stringify(fieldMappings));
    formData.append('customFields', JSON.stringify(customFields));

    const response = await api.post('/contacts/upload-csv', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  } catch (error) {
    console.error('Error uploading contacts CSV:', error);
    throw error.response?.data?.message || 'Failed to upload contacts CSV file';
  }
};

// Get HubSpot contacts import preview
export const getHubSpotContactsPreview = async (mode = 'contacts', params = {}) => {
  try {
    const response = await api.get('/contacts/import-hubspot-preview', {
      params: { mode, ...params }
    });
    console.log('📡 API response from getHubSpotContactsPreview:', {
      status: response.status,
      data: response.data,
      mode: mode
    });
    return response.data;
  } catch (error) {
    console.error('Error getting HubSpot contacts preview:', error);
    throw error.response?.data?.message || 'Failed to get HubSpot contacts preview';
  }
};

// Import contacts from HubSpot with optional field mappings
export const importContactsFromHubSpot = async (selectedContacts = [], fieldMappings = {}, customFields = {}) => {
  try {
    const response = await api.post('/contacts/import-hubspot', {
      selectedContacts,
      fieldMappings,
      customFields
    });
    return response.data;
  } catch (error) {
    console.error('Error importing contacts from HubSpot:', error);
    throw error.response?.data?.message || 'Failed to import contacts from HubSpot';
  }
};

// Import contacts from Salesforce
export const importContactsFromSalesforce = async () => {
  try {
    const response = await api.post('/contacts/import-salesforce');
    return response.data;
  } catch (error) {
    console.error('Error importing contacts from Salesforce:', error);
    throw error.response?.data?.message || 'Failed to import contacts from Salesforce';
  }
};

// Get contact by ID
export const getContact = async (id) => {
  try {
    const response = await api.get(`/contacts/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching contact:', error);
    throw error.response?.data?.message || 'Failed to fetch contact';
  }
};

// Update contact
export const updateContact = async (id, data) => {
  try {
    const response = await api.put(`/contacts/${id}`, data);
    return response.data;
  } catch (error) {
    console.error('Error updating contact:', error);
    throw error.response?.data?.message || 'Failed to update contact';
  }
};

// Delete contact
export const deleteContact = async (id) => {
  try {
    const response = await api.delete(`/contacts/${id}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting contact:', error);
    throw error.response?.data?.message || 'Failed to delete contact';
  }
};

// Search LinkedIn activity for a contact
export const searchLinkedInActivity = async (contactId, searchData) => {
  try {
    const response = await api.post(`/contacts/${contactId}/search-linkedin`, searchData);
    return response.data;
  } catch (error) {
    console.error('Error searching LinkedIn activity:', error);
    // Extract error message from response, prioritizing error field
    const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to search LinkedIn activity';
    const errorObj = new Error(errorMessage);
    errorObj.response = error.response; // Preserve response for debugging
    throw errorObj;
  }
};

// Search news mentions for a contact
export const searchNewsMentions = async (contactId, searchData) => {
  try {
    const response = await api.post(`/contacts/${contactId}/search-news`, searchData);
    return response.data;
  } catch (error) {
    console.error('Error searching news mentions:', error);
    // Return error details if available
    const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Failed to search news mentions';
    const errorObj = new Error(errorMessage);
    errorObj.response = error.response; // Preserve response for debugging
    // Return error in a format the component can handle
    return {
      success: false,
      articles: [],
      error: errorMessage
    };
  }
};

// Get contact engagements from the new contacts_engagement table
export const getContactEngagements = async (params = {}) => {
  try {
    const response = await api.get('/contacts/engagements', { params });
    return response.data;
  } catch (error) {
    console.error('Error fetching contact engagements:', error);
    throw error.response?.data?.message || 'Failed to fetch contact engagements';
  }
}; 